import React, { useContext } from 'react';
import { useAppSelector } from '@/features';
import { type Connector, type ConnectorStatusEnum, type Vehicle } from '@/types/swagger';
import { Button, Divider, Tooltip } from '@mui/material';
import CircularProgress from 'components/CircularProgress';
import { CHARGING_PERMISSION } from 'constants/ChargingPermission';
import { UserSessionContext } from 'providers/UserSessionProvider';
import { getClassByStatus } from 'util/getComponentIcon';
import IntlMessages from 'util/IntlMessages';

import ConnectorInfo from './ConnectorInfo';
import ConnectorTitle from './ConnectorTitle';
import FinalSoc from './FinalSoc';
import ManualFields from './ManualFields';
import { ConnectorDetailsContainer, ConnectorInfoContainer } from './styled';

interface ConnectorAvailableProps {
  status: ConnectorStatusEnum;
  connector: Connector | null;
  isLoading: boolean;
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (vehicle: Vehicle) => void;
  odometerValue: string;
  setOdometerValue: (value: string) => void;
  socLimit: number;
  setSocLimit: (value: number) => void;
  prevSocLimit: number;
  handleUpdateCharge: () => void;
  handleToggleCharge: () => Promise<void>;
}

const ConnectorAvailable: React.FC<ConnectorAvailableProps> = ({
  status,
  connector,
  isLoading,
  selectedVehicle,
  setSelectedVehicle,
  odometerValue,
  setOdometerValue,
  socLimit,
  setSocLimit,
  prevSocLimit,
  handleUpdateCharge,
  handleToggleCharge,
}) => {
  const { user } = useContext(UserSessionContext);
  const chargePoint = useAppSelector((state) =>
    connector?.charge_point ? state.chargePoints.byId[connector.charge_point] : null
  );

  return (
    <ConnectorDetailsContainer className="connector-details-container flex-fill">
      <ConnectorTitle connectorName={connector?.verbose_name || '- - -'} />
      <ConnectorInfoContainer className={getClassByStatus(status)}>
        <ConnectorInfo connector={connector} status={status} />
      </ConnectorInfoContainer>
      <div className="d-flex flex-column flex-fill">
        {!isLoading ? (
          <div className="d-flex flex-column flex-fill justify-content-end">
            {chargePoint?.fill_manual_fields && (
              <ManualFields
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                odometerValue={odometerValue}
                setOdometerValue={setOdometerValue}
              />
            )}
            <Divider className="my-3" />
            <div className="connector-details-actions">
              <FinalSoc
                disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
                socLimit={socLimit}
                setSocLimit={setSocLimit}
              />
              <Tooltip
                disableInteractive
                disableHoverListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
                disableTouchListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
                title={<IntlMessages id="components.connector-details.charging-permission" />}
              >
                <div>
                  <Tooltip
                    disableInteractive
                    disableHoverListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    disableTouchListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    title={<IntlMessages id="components.connector-details.update-charge-tooltip" />}
                    placement="top"
                  >
                    <div className="mb-3">
                      <Button
                        size="medium"
                        variant="outlined"
                        fullWidth
                        disabled={
                          !user?.groups?.includes(CHARGING_PERMISSION) || socLimit === prevSocLimit
                        }
                        onClick={handleUpdateCharge}
                      >
                        <IntlMessages id="components.connector-details.update-charge" />
                      </Button>
                    </div>
                  </Tooltip>
                  <Tooltip
                    disableInteractive
                    disableHoverListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) ||
                      !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                    }
                    disableTouchListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) ||
                      !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                    }
                    title={<IntlMessages id="components.connector-details.manual-fields-filled" />}
                  >
                    <div>
                      <Button
                        className="w-100"
                        size="medium"
                        variant="contained"
                        color="primary"
                        disabled={
                          !user?.groups?.includes(CHARGING_PERMISSION) ||
                          (chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                        }
                        onClick={handleToggleCharge}
                      >
                        <IntlMessages id="components.connector-details.start-charge" />
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <CircularProgress />
          </div>
        )}
      </div>
    </ConnectorDetailsContainer>
  );
};

export default ConnectorAvailable;
